import React, { useState, useEffect, useCallback } from 'react';
import ConfirmModal from './ConfirmModal';

const getToken = () => localStorage.getItem('token');

export default function SettingsMessageRelay({ isActive, setError, setSuccess }) {
  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  const [draft, setDraft] = useState(null);
  const [newCh, setNewCh] = useState({ channel_id: '', label: '' });
  const [kwPhrase, setKwPhrase] = useState('');

  const [showFolderModal, setShowFolderModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
  });
  const [newFolder, setNewFolder] = useState({
    display_name: '',
    source_guild_id: '',
    target_channel_id: '',
    alert_role_id: '',
    enabled: true,
  });

  const flashSuccess = useCallback(
    (msg) => {
      setSuccess(msg);
      setTimeout(() => setSuccess(''), 2200);
    },
    [setSuccess],
  );

  const openConfirm = useCallback((title, message, onConfirm) => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      onConfirm: async () => {
        try {
          await onConfirm();
        } finally {
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      },
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/message-relay', { headers: { Authorization: `Bearer ${getToken()}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Не удалось загрузить трансляцию');
      setFolders(Array.isArray(data.folders) ? data.folders : []);
      setKeywords(Array.isArray(data.keywords) ? data.keywords : []);
      setExpandedId(null);
      setDraft(null);
    } catch (e) {
      setError(e.message);
      setTimeout(() => setError(''), 4000);
    } finally {
      setLoading(false);
    }
  }, [setError]);

  useEffect(() => {
    if (isActive) load();
  }, [isActive, load]);

  const toggleExpand = (f) => {
    if (expandedId === f.id) {
      setExpandedId(null);
      setDraft(null);
      setNewCh({ channel_id: '', label: '' });
      return;
    }
    setExpandedId(f.id);
    setDraft({
      folderId: f.id,
      display_name: f.display_name,
      source_guild_id: f.source_guild_id,
      target_channel_id: f.target_channel_id,
      alert_role_id: f.alert_role_id || '',
      enabled: f.enabled !== false,
    });
    setNewCh({ channel_id: '', label: '' });
  };

  const saveFolderDraft = async (folderId) => {
    try {
      const form = (({ folderId: _id, ...rest }) => rest)(draft);
      const res = await fetch(`/api/message-relay/folders/${folderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          display_name: form.display_name.trim(),
          source_guild_id: form.source_guild_id.trim(),
          target_channel_id: form.target_channel_id.trim(),
          enabled: !!form.enabled,
          alert_role_id: (form.alert_role_id || '').trim() || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Не сохранено');
      flashSuccess('Папка обновлена');
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const patchFolderEnabled = async (folderId, enabled) => {
    try {
      const res = await fetch(`/api/message-relay/folders/${folderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ enabled: !!enabled }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Не сохранено');
      setFolders((prev) => prev.map((f) => (f.id === folderId ? { ...f, enabled: !!enabled } : f)));
      setDraft((d) => (d && d.folderId === folderId ? { ...d, enabled: !!enabled } : d));
    } catch (e) {
      setError(e.message);
    }
  };

  const deleteFolder = async (folderId, name) => {
    try {
      const res = await fetch(`/api/message-relay/folders/${folderId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Не удалось удалить');
      flashSuccess('Папка удалена');
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const addFolder = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/message-relay/folders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          display_name: newFolder.display_name.trim(),
          source_guild_id: newFolder.source_guild_id.trim(),
          target_channel_id: newFolder.target_channel_id.trim(),
          alert_role_id: newFolder.alert_role_id.trim() || null,
          enabled: !!newFolder.enabled,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Не создано');
      flashSuccess('Папка создана');
      setShowFolderModal(false);
      setNewFolder({
        display_name: '',
        source_guild_id: '',
        target_channel_id: '',
        alert_role_id: '',
        enabled: true,
      });
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const addChannel = async (e, folderId) => {
    e.preventDefault();
    try {
      const res = await fetch(`/api/message-relay/folders/${folderId}/channels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          channel_id: newCh.channel_id.trim(),
          label: newCh.label.trim() || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Не добавлено');
      flashSuccess('Канал добавлен');
      setNewCh({ channel_id: '', label: '' });
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const removeChannel = async (folderId, channelId) => {
    try {
      const res = await fetch(`/api/message-relay/folders/${folderId}/channels/${channelId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Не удалено');
      flashSuccess('Канал исключён');
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const addKeyword = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/message-relay/keywords', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ phrase: kwPhrase.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Не добавлено');
      flashSuccess('Фраза добавлена');
      setKwPhrase('');
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const removeKeyword = async (id) => {
    try {
      const res = await fetch(`/api/message-relay/keywords/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Не удалено');
      flashSuccess('Фраза удалена');
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  if (!isActive) return null;

  if (loading && folders.length === 0 && keywords.length === 0) {
    return (
      <div className="settings-relay-loading loading relay-loading-wrap">
        <div className="relay-loading-spinner" aria-hidden />
        <span>Загрузка…</span>
      </div>
    );
  }

  const expandedFolder = folders.find((f) => f.id === expandedId);

  return (
    <div className="settings-message-relay settings-relay-root">
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
      />

      <section className="settings-form-card relay-card relay-card-sources settings-relay-folders-card">
        <div className="card-header settings-relay-folders-heading relay-card-head-row">
          <div className="relay-card-head-main">
            <h3 className="relay-card-h">Источники</h3>
          </div>
          <button type="button" className="btn-add-server btn-relay-create relay-btn-accent" onClick={() => setShowFolderModal(true)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            <span>Добавить</span>
          </button>
        </div>

        <div className="settings-relay-folder-list">
          {folders.length === 0 ? (
            <div className="relay-empty-state relay-empty-state--minimal">Нет источников</div>
          ) : (
            folders.map((f) => {
              const open = expandedId === f.id;
              const chCount = (f.channels || []).filter((ch) => ch.enabled !== false).length;
              return (
                <div key={f.id} className={`relay-folder-sheet ${open ? 'relay-folder-open' : ''}`}>
                  <div className="relay-folder-header-row">
                    <button
                      type="button"
                      className="relay-folder-bar"
                      onClick={() => toggleExpand(f)}
                      aria-expanded={open}
                    >
                      <div className="relay-folder-bar-main">
                        <div className="relay-folder-title-row">
                          <span className="relay-folder-title">{f.display_name}</span>
                          <span className="relay-folder-chips" aria-label={`Каналов: ${chCount}`}>
                            <span className="relay-chip-mini">{chCount} кан.</span>
                          </span>
                        </div>
                        <span className="relay-folder-meta">
                          <code>{f.source_guild_id}</code>
                          <span className="relay-meta-arrow" aria-hidden />
                          <code>{f.target_channel_id}</code>
                        </span>
                      </div>
                      <svg
                        className={`relay-chevron ${open ? 'relay-chevron-open' : ''}`}
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                    <div
                      className="relay-folder-side-controls"
                      onClick={(ev) => ev.stopPropagation()}
                      onKeyDown={(ev) => ev.stopPropagation()}
                      role="presentation"
                    >
                      <label className="relay-source-toggle-label" htmlFor={`relay-en-${f.id}`}>
                        <input
                          id={`relay-en-${f.id}`}
                          type="checkbox"
                          className="display-settings-toggle relay-source-toggle"
                          aria-label="Включить источник"
                          checked={f.enabled !== false}
                          onChange={(e) => patchFolderEnabled(f.id, e.target.checked)}
                        />
                      </label>
                    </div>
                  </div>

                  {open && draft && expandedFolder?.id === f.id && (
                    <div className="relay-folder-panel">
                      <div className="form-grid relay-folder-grid-top">
                        <div className="form-group">
                          <label htmlFor={`dn-${f.id}`}>Название</label>
                          <input
                            id={`dn-${f.id}`}
                            value={draft.display_name}
                            onChange={(e) => setDraft((d) => ({ ...d, display_name: e.target.value }))}
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor={`guild-${f.id}`}>ID сервера</label>
                          <input
                            id={`guild-${f.id}`}
                            value={draft.source_guild_id}
                            inputMode="numeric"
                            onChange={(e) => setDraft((d) => ({ ...d, source_guild_id: e.target.value }))}
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor={`to-${f.id}`}>Канал назначения</label>
                          <input
                            id={`to-${f.id}`}
                            value={draft.target_channel_id}
                            inputMode="numeric"
                            onChange={(e) => setDraft((d) => ({ ...d, target_channel_id: e.target.value }))}
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor={`role-${f.id}`}>ID роли (пинг)</label>
                          <input
                            id={`role-${f.id}`}
                            value={draft.alert_role_id}
                            inputMode="numeric"
                            onChange={(e) => setDraft((d) => ({ ...d, alert_role_id: e.target.value }))}
                          />
                          <p className="form-hint" style={{ marginTop: 6, opacity: 0.75, fontSize: 12 }}>
                            Пинг при триггере слов, вложении или теге роли в исходном чате.
                          </p>
                        </div>
                      </div>

                      <div className="relay-folder-actions-row">
                        <button
                          type="button"
                          className="btn btn-primary relay-folder-btn-save"
                          onClick={() => saveFolderDraft(f.id)}
                        >
                          Сохранить изменения
                        </button>
                        <button
                          type="button"
                          className="relay-folder-btn-remove"
                          onClick={() =>
                            openConfirm(
                              'Удаление источника',
                              `Удалить источник «${f.display_name}» и все его каналы?`,
                              () => deleteFolder(f.id, f.display_name),
                            )
                          }
                        >
                          Удалить источник
                        </button>
                      </div>

                      <div className="relay-channels-section relay-subpanel">
                        <h4 className="relay-subheading relay-subheading-inline">Каналы</h4>
                        {(f.channels || []).filter((ch) => ch.enabled !== false).length === 0 ? (
                          <p className="relay-hint-muted">Нет каналов</p>
                        ) : (
                          <ul className="relay-channel-list">
                            {(f.channels || [])
                              .filter((ch) => ch.enabled !== false)
                              .map((ch) => (
                                <li key={ch.channel_id} className="relay-channel-item">
                                  <code>{ch.channel_id}</code>
                                  {ch.label && <span className="relay-ch-label">{ch.label}</span>}
                                  <button
                                    type="button"
                                    className="relay-btn-delete relay-ch-remove"
                                    onClick={() =>
                                      openConfirm(
                                        'Удаление канала',
                                        `Убрать канал ${ch.channel_id} из источника?`,
                                        () => removeChannel(f.id, ch.channel_id),
                                      )
                                    }
                                  >
                                    Убрать
                                  </button>
                                </li>
                              ))}
                          </ul>
                        )}
                        <form onSubmit={(ev) => addChannel(ev, f.id)} className="relay-add-ch-form">
                          <input
                            type="text"
                            aria-label="ID канала"
                            placeholder="ID канала (17-20 цифр)"
                            value={newCh.channel_id}
                            onChange={(e) => setNewCh((ch) => ({ ...ch, channel_id: e.target.value }))}
                          />
                          <input
                            type="text"
                            aria-label="Подпись"
                            placeholder="Название канала (необязательно)"
                            value={newCh.label}
                            onChange={(e) => setNewCh((ch) => ({ ...ch, label: e.target.value }))}
                          />
                          <button type="submit" className="btn btn-secondary relay-add-ch-submit">
                            Добавить
                          </button>
                        </form>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </section>

      <section className="settings-form-card relay-card relay-card-keywords">
        <div className="card-header relay-card-head-row relay-kw-head">
          <div className="relay-card-head-main">
            <h3 className="relay-card-h">Триггеры</h3>
          </div>
        </div>

        <form onSubmit={addKeyword} className="relay-kw-toolbar">
          <div className="relay-kw-input-wrap">
            <input
              type="text"
              className="relay-kw-input"
              aria-label="Триггер"
              value={kwPhrase}
              onChange={(e) => setKwPhrase(e.target.value)}
            />
          </div>
          <button type="submit" className="btn btn-primary relay-kw-add-btn">
            Добавить
          </button>
        </form>

        {keywords.length > 0 ? (
          <ul className="relay-kw-chips" aria-label="Список триггеров">
            {keywords.map((k) => (
              <li key={k.id} className="relay-kw-chip">
                <span className="relay-kw-chip-text">
                  <code>{k.phrase}</code>
                </span>
                <button
                  type="button"
                  className="relay-kw-remove"
                  onClick={() =>
                    openConfirm('Удаление триггера', `Удалить триггер «${k.phrase}»?`, () => removeKeyword(k.id))
                  }
                  title="Удалить"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="relay-kw-placeholder">Нет триггеров</p>
        )}
      </section>

      {showFolderModal && (
        <div className="form-modal-overlay relay-modal-overlay" onClick={() => setShowFolderModal(false)}>
          <div className="form-modal relay-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="relay-modal-title">
            <div className="form-modal-header relay-modal-top">
              <div>
                <h3 id="relay-modal-title">Источник</h3>
              </div>
              <button type="button" className="btn-close-form" onClick={() => setShowFolderModal(false)} title="Закрыть">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <form onSubmit={addFolder} className="form-modal-body relay-modal-body">
              <div className="form-group">
                <label htmlFor="nf-name">Название</label>
                <input
                  id="nf-name"
                  required
                  value={newFolder.display_name}
                  onChange={(e) => setNewFolder((s) => ({ ...s, display_name: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label htmlFor="nf-guild">ID сервера</label>
                <input
                  id="nf-guild"
                  required
                  inputMode="numeric"
                  value={newFolder.source_guild_id}
                  onChange={(e) => setNewFolder((s) => ({ ...s, source_guild_id: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label htmlFor="nf-target">Канал назначения</label>
                <input
                  id="nf-target"
                  required
                  inputMode="numeric"
                  value={newFolder.target_channel_id}
                  onChange={(e) => setNewFolder((s) => ({ ...s, target_channel_id: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label htmlFor="nf-role">ID роли (пинг)</label>
                <input
                  id="nf-role"
                  inputMode="numeric"
                  value={newFolder.alert_role_id}
                  onChange={(e) => setNewFolder((s) => ({ ...s, alert_role_id: e.target.value }))}
                />
              </div>
              <div className="relay-modal-toggle-row">
                <span className="relay-modal-toggle-text">Включено</span>
                <label className="relay-source-toggle-label" htmlFor="nf-enabled">
                  <input
                    id="nf-enabled"
                    type="checkbox"
                    className="display-settings-toggle relay-source-toggle"
                    checked={newFolder.enabled}
                    onChange={(e) => setNewFolder((s) => ({ ...s, enabled: e.target.checked }))}
                  />
                </label>
              </div>
              <div className="form-actions relay-modal-actions">
                <button type="submit" className="btn btn-primary">
                  Добавить
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowFolderModal(false)}>
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
